// FILE: src/admin/pages/support/owners/tabs/SupportOwnerNotesTab.jsx
import React, { useEffect, useRef, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { createSupportOwnerNote, listSupportOwnerNotes } from "../../../../../api/adminSupportApi";

function dt(v) {
  if (!v) return "—";
  try {
    const d = new Date(String(v));
    if (!Number.isNaN(d.getTime())) return d.toLocaleString();
  } catch (_) {}
  return String(v);
}

function Footer({ page, size, total, totalPages, setPage, setSize }) {
  const from = total ? page * size + 1 : 0;
  const to = total ? Math.min(total, (page + 1) * size) : 0;

  return (
    <div className="px-5 py-3 border-t border-slate-200 bg-white">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="text-xs text-slate-500">
          {total ? (
            <>
              Showing <span className="font-semibold text-slate-700">{from}</span>–<span className="font-semibold text-slate-700">{to}</span> of{" "}
              <span className="font-semibold text-slate-700">{total}</span>
            </>
          ) : (
            "No records"
          )}
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex items-center gap-2">
            <div className="text-xs text-slate-500">View</div>
            <div className="relative">
              <select
                value={size}
                onChange={(e) => setSize(Number(e.target.value))}
                className="appearance-none h-10 rounded-md border border-slate-200 bg-white pl-3 pr-9 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              >
                {[10, 20, 30, 50, 100].map((n) => (
                  <option key={n} value={n}>
                    {n} / page
                  </option>
                ))}
              </select>
              <span className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-slate-500">▼</span>
            </div>
          </div>

          <div className="text-sm text-slate-700">
            Page <span className="font-semibold">{page + 1}</span> of <span className="font-semibold">{Math.max(1, totalPages || 1)}</span>
          </div>

          <div className="flex items-center gap-2">
            <button
              className="h-10 px-4 rounded-md border border-slate-200 text-sm font-semibold hover:bg-slate-50 disabled:opacity-50"
              disabled={page <= 0}
              onClick={() => setPage(page - 1)}
            >
              Prev
            </button>
            <button
              className="h-10 px-4 rounded-md border border-slate-200 text-sm font-semibold hover:bg-slate-50 disabled:opacity-50"
              disabled={totalPages ? page >= totalPages - 1 : true}
              onClick={() => setPage(page + 1)}
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function SupportOwnerNotesTab() {
  const { ownerId } = useOutletContext();

  const [text, setText] = useState("");
  const [creating, setCreating] = useState(false);

  const [page, setPage] = useState(0);
  const [size, setSize] = useState(30);

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const reqRef = useRef(0);

  const load = async () => {
    const rid = ++reqRef.current;
    setBusy(true);
    setErr("");
    try {
      const res = await listSupportOwnerNotes({ ownerId, page, size });
      if (rid !== reqRef.current) return;

      const content = Array.isArray(res?.content) ? res.content : [];
      setRows(content);
      setTotal(Number(res?.totalElements ?? content.length ?? 0));
      setTotalPages(Number(res?.totalPages ?? 0));
    } catch (e) {
      setErr(e?.message || "Failed to load notes");
      setRows([]);
      setTotal(0);
      setTotalPages(0);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ownerId, page, size]);

  const create = async () => {
    const n = String(text || "").trim();
    if (!n) return;
    setCreating(true);
    setErr("");
    try {
      await createSupportOwnerNote({ ownerId, note: n });
      setText("");
      setPage(0);
      await load();
    } catch (e) {
      setErr(e?.message || "Failed to create note");
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="min-w-0">
      <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
        <div className="border-b border-slate-200 px-5 py-4">
          <div className="text-sm font-extrabold text-slate-900">Notes</div>
          <div className="mt-1 text-xs text-slate-500">Support-only notes stored on owner record.</div>
        </div>

        <div className="px-5 py-4 border-b border-slate-200">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
            <div className="w-full">
              <label className="text-xs font-semibold text-slate-600">Add note</label>
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Write a support note…"
                className="mt-1 w-full min-h-[90px] rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 outline-none"
              />
            </div>

            <button
              onClick={create}
              disabled={creating || !String(text || "").trim()}
              className="h-10 rounded-xl bg-slate-900 px-4 text-sm font-semibold text-white hover:opacity-95 disabled:opacity-60"
            >
              {creating ? "Saving…" : "Save note"}
            </button>
          </div>

          {err ? <div className="mt-3 text-sm font-semibold text-rose-600">{err}</div> : null}
        </div>

        <div className="divide-y divide-slate-200">
          {busy ? (
            <div className="px-5 py-8 text-sm text-slate-600">Loading…</div>
          ) : rows.length === 0 ? (
            <div className="px-5 py-10 text-center">
              <div className="text-sm font-semibold text-slate-900">No notes</div>
              <div className="mt-1 text-xs text-slate-500">Add the first support note for this owner.</div>
            </div>
          ) : (
            rows.map((r) => (
              <div key={r.id} className="px-5 py-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-slate-900">{r.note || "—"}</div>
                    <div className="mt-1 text-xs text-slate-500">
                      By Admin <span className="font-semibold text-slate-700 tabular-nums">{r.createdByAdminId ?? "—"}</span>
                      <span className="mx-2 text-slate-300">•</span>
                      <span className="tabular-nums">{dt(r.createdAt)}</span>
                    </div>
                  </div>
                  <div className="text-xs text-slate-400 tabular-nums">#{r.id}</div>
                </div>
              </div>
            ))
          )}
        </div>

        <Footer
          page={page}
          size={size}
          total={total}
          totalPages={totalPages}
          setPage={setPage}
          setSize={(n) => {
            setPage(0);
            setSize(n);
          }}
        />
      </div>
    </div>
  );
}
